import { useMemo } from "react"
import { DashBoardConditions } from "features/dashBoard/types"
import {
	CommonGraphStyle,
	selectedItemStyle,
	filter
 } from "../../common"
 import { useBarGraphOption } from "./options"
import { DownLoadData, DownLoadHeader, DownLoadRecord } from "components/download/csv/types"

type useBarGraphModelProps<T> = {
	dataList: T[] | null
	conditions: DashBoardConditions,
	filterText: string | undefined
	graphOption: ReturnType<typeof useBarGraphOption<T>>
}
export const useBarGraphModel = <T,>({
	dataList,
	conditions,
	filterText,
	graphOption: {
		configure, targetList,
    axisDef, stackDef, valueDef,
    formula, sort
  }
}: useBarGraphModelProps<T>) => useMemo(
	() => {
		if (!dataList || dataList.length === 0) return null
		if (!axisDef?.key || !valueDef?.key) return null

		const condition = conditions[axisDef.key]
		const stackFunc = stackDef ? stackDef.val : configure.stack

		const data: {
			categoryCode?: string
			categoryName?: string
			code: string
			name: string
			data: {
				[index: string]: {
					value: number
					count: number
				}
			}
			total: number
			itemStyle?: CommonGraphStyle
		}[] = []
		const series: {
			code: string
			name?: string
			total: number
		}[] = []
		for (const record of dataList) {
			const axis = axisDef.val(record)
			if (!axis) continue

			const value = valueDef.val(record)
			if (value === undefined || value === 0 || !filter(record, configure, conditions, axisDef.key, targetList)) continue

			const stack = stackFunc ? stackFunc(record) : { code: '***', name: '' }
			if (!stack) continue

			const stackList = Array.isArray(stack) ? stack : [stack]
			const axisList = Array.isArray(axis) ? axis : [axis]
			for (const stack of stackList) {
				let its = series.find(s => s.code === stack.code)
				if (!its) {
					its = {
						code: stack.code,
						name: stack.name,
						total: 0
					}
					series.push(its)
				}
				its.total = its.total + value

				for (const ax of axisList) {
					if (filterText && ax.name) {
						const axName = ax.name
						if (filterText.split(' ').every(t => axName.indexOf(t) < 0)) continue
					}
					let it = data.find(r => r.categoryCode === ax.category?.code && r.code === ax.code)
					if (!it) {
						it = {
							categoryCode: ax.category?.code,
							categoryName: ax.category?.name,
							code: ax.code,
							name: ax.name ?? '',
							data: {},
							total: value,
							itemStyle: selectedItemStyle(condition, ax)
						}
						data.push(it)
					} else {
						if (formula === 'max') {
							if (it.total < value) it.total = value
						} else if (formula === 'min') {
							if (it.total > value) it.total = value
						} else {
							it.total = it.total + value
						}
					}
					let itd = it.data[stack.code]
					if (!itd) {
						itd = {
							value: value,
							count: 1
						}
						it.data[stack.code] = itd
					} else {
						if (formula === 'max') {
							if (itd.value < value) itd.value = value
						} else if (formula === 'min') {
							if (itd.value > value) itd.value = value
						} else {
							itd.value = itd.value + value
						}
						itd.count = itd.count + 1
					}
				}
			}
		}
		if (formula === 'ave') {
			data.forEach(r => {
				r.total = 0
				Object.keys(r.data).forEach(
					stackCode => {
						const it = r.data[stackCode]
						it.value = it.value / it.count
						r.total += it.value
					}
				)
			})
		}
		if (sort) {
			data.sort((a, b) => b.total - a.total)
		} else {
			data.sort((a, b) =>
				(a.categoryCode && b.categoryCode && a.categoryCode !== b.categoryCode) ?
					a.categoryCode.localeCompare(b.categoryCode) :
					a.code.localeCompare(b.code))
		}
		if (series.length === 0) {
			series.push({ code: '**', name: '', total: 0 })
		}
		series.sort((a, b) => a.code.localeCompare(b.code))

		return {
			data,
			series,
			max: Math.max(...data.map(data => data.total)),
			csvBuilder: (): DownLoadData | undefined => {
				if (!axisDef?.key || !valueDef?.key) return undefined

				const axisKey = axisDef.key
				const valueKey = valueDef.key
				const unit = valueDef.unit
	
				const header: DownLoadHeader[] = [
					{ title: axisDef.title, key: axisKey },
					{ title: valueDef.title, key: valueKey, unit }
				]
				series.forEach((series) => {
					header.push({
						title: series.name ?? '',
						key: series.code === '***' ? '' : series.code,
						unit: valueDef.unit
					})
				})
				let previousAxis = ''
				const body = data.map(
					(data) => {
						const row: DownLoadRecord = {}
						row[axisKey] = previousAxis === data.name ? '' : data.name
						row[valueKey] = data.total
						series.forEach((series) => {
							row[series.code] = data.data[series.code]?.count ?? 0
						})
						previousAxis = data.name
						return row
					}
				)
				return { header, body }
			}
		}
	},
	[configure, dataList, conditions, axisDef, valueDef, stackDef, formula, sort, filterText, targetList]
)
